import React, { useState, useEffect, useCallback } from 'react';
import GanttProjet from './GanttProjet';
import ganttApi from '../services/gantt';
import './Projets.css';

const TACHE_STATUTS = [
  { id: 'a_faire', label: 'A faire', color: '#9ca3af' },
  { id: 'fait', label: 'Fait', color: '#10b981' },
  { id: 'bloque', label: 'Bloque', color: '#ef4444' },
];

const ACTION_STATUTS = [
  { id: 'a_faire', label: 'A faire', color: '#9ca3af' },
  { id: 'en_cours', label: 'En cours', color: '#3b82f6' },
  { id: 'fait', label: 'Fait', color: '#10b981' },
  { id: 'bloque', label: 'Bloque', color: '#ef4444' },
];

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

export default function Projets({ user, isAdmin }) {
  const [activeView, setActiveView] = useState('planning');

  // Suivi data
  const [projets, setProjets] = useState([]);
  const [projetId, setProjetId] = useState(null);
  const [projet, setProjet] = useState(null);
  const [phases, setPhases] = useState([]);
  const [actions, setActions] = useState([]);
  const [taches, setTaches] = useState([]);
  const [risques, setRisques] = useState([]);
  const [collapsed, setCollapsed] = useState({});
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  // Inline edit
  const [editingTache, setEditingTache] = useState(null);
  const [editTacheVal, setEditTacheVal] = useState({});
  const [addingTacheFor, setAddingTacheFor] = useState(null);
  const [newTache, setNewTache] = useState({ titre: '', responsable: '', echeance: '' });

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // Load projets list
  useEffect(() => {
    ganttApi.getProjets().then(r => {
      setProjets(r.data);
      if (r.data.length > 0 && !projetId) setProjetId(r.data[0].id);
    }).catch(() => {});
  }, []); // eslint-disable-line

  // Load full project for suivi
  const loadSuivi = useCallback(async () => {
    if (!projetId) return;
    setLoading(true);
    try {
      const r = await ganttApi.getProjetFull(projetId);
      setProjet(r.data);
      setPhases(r.data.phases || []);
      setActions(r.data.actions || []);
      setTaches(r.data.taches || []);
      setRisques(r.data.risques || []);
    } catch { showToast('Erreur chargement', 'error'); }
    setLoading(false);
  }, [projetId, showToast]);

  useEffect(() => { loadSuivi(); }, [loadSuivi]);

  // Taches CRUD
  const handleAddTache = async (actionId) => {
    if (!newTache.titre.trim()) return;
    try {
      await ganttApi.createTache({
        action_id: actionId,
        titre: newTache.titre,
        responsable: newTache.responsable || null,
        echeance: newTache.echeance || null,
      });
      setAddingTacheFor(null);
      setNewTache({ titre: '', responsable: '', echeance: '' });
      await loadSuivi();
    } catch { showToast('Erreur', 'error'); }
  };

  const handleUpdateTache = async (id, data) => {
    try {
      await ganttApi.updateTache(id, data);
      await loadSuivi();
    } catch { showToast('Erreur', 'error'); }
  };

  const handleDeleteTache = async (id) => {
    try {
      await ganttApi.deleteTache(id);
      setEditingTache(null);
      await loadSuivi();
    } catch { showToast('Erreur', 'error'); }
  };

  const handleSaveEditTache = async () => {
    if (!editingTache) return;
    try {
      await ganttApi.updateTache(editingTache, editTacheVal);
      setEditingTache(null);
      await loadSuivi();
    } catch { showToast('Erreur', 'error'); }
  };

  const toggleCollapse = (phaseId) => {
    setCollapsed(prev => ({ ...prev, [phaseId]: !prev[phaseId] }));
  };

  // Quick toggle statut
  const toggleTacheStatut = async (tache) => {
    const next = tache.statut === 'fait' ? 'a_faire' : 'fait';
    await handleUpdateTache(tache.id, { statut: next });
  };

  // ═══════ IMPRESSION ═══════
  const handlePrint = async () => {
    // Charger les données si pas encore fait
    let pData = projet, pPhases = phases, pActions = actions, pTaches = taches, pRisques = risques;
    if (!pData && projetId) {
      try {
        const r = await ganttApi.getProjetFull(projetId);
        pData = r.data;
        pPhases = r.data.phases || [];
        pActions = r.data.actions || [];
        pTaches = r.data.taches || [];
        pRisques = r.data.risques || [];
      } catch { showToast('Erreur chargement pour impression', 'error'); return; }
    }
    if (!pData) { showToast('Aucun projet a imprimer', 'error'); return; }

    const statutLabel = (s) => ACTION_STATUTS.find(x => x.id === s)?.label || s;
    const statutColor = (s) => ACTION_STATUTS.find(x => x.id === s)?.color || '#9ca3af';
    const tacheStatutLabel = (s) => TACHE_STATUTS.find(x => x.id === s)?.label || s;
    const risqueStatutLabel = (s) => ({ ouvert: 'Ouvert', surveille: 'Surveille', clos: 'Clos' }[s] || s);
    const risqueColor = (s) => ({ ouvert: '#ef4444', surveille: '#f59e0b', clos: '#10b981' }[s] || '#9ca3af');

    // Générer les semaines pour le Gantt
    const mondayOf = (date) => {
      const d = new Date(date); const day = d.getDay();
      d.setDate(d.getDate() - day + (day === 0 ? -6 : 1));
      return d;
    };
    const projStart = mondayOf(pData.date_debut);
    const nbWeeks = pData.nb_semaines || 12;
    const totalDays = nbWeeks * 7;
    const weeksHtml = Array.from({ length: nbWeeks }, (_, i) => {
      const d = new Date(projStart); d.setDate(d.getDate() + i * 7);
      return `<th style="min-width:40px;font-size:8px;padding:2px;text-align:center;border:1px solid #e2e8f0;background:#f8fafc;">S${i + 1}</th>`;
    }).join('');

    const diffDays = (a, b) => Math.round((new Date(b) - new Date(a)) / 86400000);

    const getBar = (a) => {
      if (!a.date_debut) return '';
      const start = diffDays(projStart, new Date(a.date_debut));
      const end = a.date_fin ? diffDays(projStart, new Date(a.date_fin)) + 1 : start + 1;
      const left = Math.max(0, (start / totalDays) * 100);
      const width = Math.max(1, ((end - start) / totalDays) * 100);
      if (a.jalon) return `<div style="position:absolute;left:${left}%;top:50%;transform:translate(-50%,-50%);color:#f59e0b;font-size:14px;">&#9670;</div>`;
      return `<div style="position:absolute;left:${left}%;width:${width}%;top:50%;transform:translateY(-50%);height:10px;background:${statutColor(a.statut)};border-radius:3px;"></div>`;
    };

    // Page 1 : Planning
    let ganttRows = '';
    pPhases.forEach(phase => {
      const phaseActions = pActions.filter(a => a.phase_id === phase.id);
      ganttRows += `<tr><td style="padding:4px 8px;font-weight:700;font-size:9px;border-left:3px solid ${phase.couleur};background:${phase.couleur}0d;white-space:nowrap;">${phase.nom}</td>`;
      for (let i = 0; i < nbWeeks; i++) ganttRows += `<td style="border:1px solid #f1f5f9;background:${phase.couleur}08;"></td>`;
      ganttRows += `</tr>`;
      phaseActions.forEach(a => {
        ganttRows += `<tr><td style="padding:3px 8px 3px 20px;font-size:8px;white-space:nowrap;border-bottom:1px solid #f8fafc;">${a.vigilance ? '<span style="color:#d97706;font-weight:800;">! </span>' : ''}${a.nom}</td>`;
        ganttRows += `<td colspan="${nbWeeks}" style="position:relative;border:1px solid #f8fafc;height:16px;">${getBar(a)}</td></tr>`;
      });
    });

    // Légende
    const legendeHtml = ACTION_STATUTS.map(s => `<span style="display:inline-flex;align-items:center;gap:4px;margin-right:12px;"><span style="width:10px;height:10px;border-radius:50%;background:${s.color};display:inline-block;"></span>${s.label}</span>`).join('')
      + `<span style="display:inline-flex;align-items:center;gap:4px;margin-right:12px;color:#f59e0b;">&#9670; Jalon</span>`
      + `<span style="display:inline-flex;align-items:center;gap:4px;color:#d97706;font-weight:800;">! Vigilance</span>`;

    // Risques
    let risquesHtml = '';
    if (pRisques.length > 0) {
      risquesHtml = `<h3 style="font-size:11px;margin:16px 0 6px;color:#1e293b;">Risques & vigilance</h3>
        <table style="width:100%;border-collapse:collapse;font-size:8px;">
          <thead><tr style="background:#f8fafc;">
            <th style="text-align:left;padding:4px 6px;border:1px solid #e2e8f0;">Sujet</th>
            <th style="text-align:left;padding:4px 6px;border:1px solid #e2e8f0;">Statut</th>
            <th style="text-align:left;padding:4px 6px;border:1px solid #e2e8f0;">Derniere revue</th>
            <th style="text-align:left;padding:4px 6px;border:1px solid #e2e8f0;">Prochaine revue</th>
            <th style="text-align:left;padding:4px 6px;border:1px solid #e2e8f0;">Notes</th>
          </tr></thead><tbody>` +
        pRisques.map(r => `<tr>
            <td style="padding:3px 6px;border:1px solid #f1f5f9;">${r.sujet}</td>
            <td style="padding:3px 6px;border:1px solid #f1f5f9;color:${risqueColor(r.statut)};font-weight:600;">${risqueStatutLabel(r.statut)}</td>
            <td style="padding:3px 6px;border:1px solid #f1f5f9;">${formatDate(r.date_revue)}</td>
            <td style="padding:3px 6px;border:1px solid #f1f5f9;">${formatDate(r.date_prochaine_revue)}</td>
            <td style="padding:3px 6px;border:1px solid #f1f5f9;">${r.notes || '-'}</td>
          </tr>`).join('') +
        `</tbody></table>`;
    }

    // Page 2 : Suivi détaillé (tout dégroupé)
    let suiviHtml = '';
    pPhases.forEach(phase => {
      const phaseActions = pActions.filter(a => a.phase_id === phase.id);
      suiviHtml += `<div style="margin-bottom:12px;">`;
      suiviHtml += `<div style="font-weight:700;font-size:10px;color:#1e293b;border-left:3px solid ${phase.couleur};padding:4px 8px;background:${phase.couleur}0d;margin-bottom:4px;">${phase.nom}</div>`;
      phaseActions.forEach(a => {
        const aTaches = pTaches.filter(t => t.action_id === a.id);
        const done = aTaches.filter(t => t.statut === 'fait').length;
        suiviHtml += `<div style="margin-left:12px;margin-bottom:8px;border:1px solid #f1f5f9;border-radius:4px;padding:6px 8px;">`;
        suiviHtml += `<div style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:4px;">`;
        suiviHtml += `<div style="display:flex;align-items:center;gap:4px;">`;
        if (a.vigilance) suiviHtml += `<span style="color:#d97706;font-weight:800;font-size:9px;">!</span>`;
        if (a.jalon) suiviHtml += `<span style="color:#f59e0b;">&#9670;</span>`;
        suiviHtml += `<span style="font-weight:600;font-size:9px;">${a.nom}</span>`;
        suiviHtml += `<span style="padding:1px 6px;border-radius:8px;font-size:7px;font-weight:700;background:${statutColor(a.statut)}20;color:${statutColor(a.statut)};">${statutLabel(a.statut)}</span>`;
        suiviHtml += `</div>`;
        suiviHtml += `<div style="font-size:7px;color:#6b7280;">`;
        if (a.responsable) suiviHtml += `<span style="background:#f1f5f9;padding:1px 4px;border-radius:6px;margin-right:4px;">${a.responsable}</span>`;
        suiviHtml += `${formatDate(a.date_debut)} → ${formatDate(a.date_fin)}`;
        suiviHtml += `</div></div>`;
        if (aTaches.length > 0) {
          suiviHtml += `<div style="margin-top:4px;font-size:7px;color:#94a3b8;font-weight:600;">${done}/${aTaches.length} tache(s)</div>`;
          aTaches.forEach(t => {
            const isDone = t.statut === 'fait';
            const isBlocked = t.statut === 'bloque';
            suiviHtml += `<div style="display:flex;align-items:center;gap:6px;padding:2px 0;font-size:8px;${isDone ? 'color:#94a3b8;text-decoration:line-through;' : ''}${isBlocked ? 'color:#ef4444;' : ''}">`;
            suiviHtml += `<span style="width:12px;height:12px;border:1.5px solid ${isDone ? '#10b981' : isBlocked ? '#ef4444' : '#d1d5db'};border-radius:2px;display:inline-flex;align-items:center;justify-content:center;font-size:7px;${isDone ? 'background:#10b981;color:#fff;' : ''}">${isDone ? '✓' : isBlocked ? '!' : ''}</span>`;
            suiviHtml += `<span style="flex:1;">${t.titre}</span>`;
            if (t.responsable) suiviHtml += `<span style="color:#6b7280;background:#f1f5f9;padding:0 4px;border-radius:4px;">${t.responsable}</span>`;
            if (t.echeance) suiviHtml += `<span style="color:#94a3b8;">${formatDate(t.echeance)}</span>`;
            suiviHtml += `</div>`;
          });
        }
        suiviHtml += `</div>`;
      });
      suiviHtml += `</div>`;
    });

    // Assembler et imprimer
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`<!DOCTYPE html><html><head><title>${pData.nom} — Impression</title>
      <style>
        @page { size: landscape; margin: 12mm; }
        * { box-sizing: border-box; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #1e293b; margin: 0; padding: 0; }
        .page { page-break-after: always; padding: 0; }
        .page:last-child { page-break-after: auto; }
        table { border-collapse: collapse; width: 100%; }
        @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
      </style></head><body>
      <div class="page">
        <h1 style="font-size:16px;margin:0 0 4px;">${pData.nom}</h1>
        <div style="font-size:8px;color:#6b7280;margin-bottom:8px;">Debut: ${formatDate(pData.date_debut)} | ${nbWeeks} semaines</div>
        <div style="margin-bottom:10px;font-size:8px;">${legendeHtml}</div>
        <table>
          <thead><tr><th style="text-align:left;padding:4px 8px;font-size:8px;border:1px solid #e2e8f0;background:#f8fafc;min-width:140px;">Actions</th>${weeksHtml}</tr></thead>
          <tbody>${ganttRows}</tbody>
        </table>
        ${risquesHtml}
      </div>
      <div class="page">
        <h1 style="font-size:16px;margin:0 0 4px;">${pData.nom} — Suivi detaille</h1>
        <div style="font-size:8px;color:#6b7280;margin-bottom:12px;">Imprime le ${new Date().toLocaleDateString('fr-FR')}</div>
        ${suiviHtml}
      </div>
      </body></html>`);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 300);
  };

  return (
    <div className="projets-container">
      {toast && <div className={`gantt-toast ${toast.type}`}>{toast.msg}</div>}

      {/* Sous-onglets */}
      <div className="projets-tabs">
        <button className={activeView === 'planning' ? 'active' : ''} onClick={() => setActiveView('planning')}>Planning</button>
        <button className={activeView === 'suivi' ? 'active' : ''} onClick={() => setActiveView('suivi')}>Suivi</button>
        <div className="projets-tabs-spacer" />
        {projetId && <button className="projets-print-btn" onClick={handlePrint}>Imprimer</button>}
      </div>

      {/* Vue Planning = Gantt */}
      {activeView === 'planning' && <GanttProjet user={user} isAdmin={isAdmin} />}

      {/* Vue Suivi */}
      {activeView === 'suivi' && (
        <div className="suivi-container">
          {/* Sélecteur projet */}
          {projets.length > 0 && (
            <div className="suivi-header">
              <select className="suivi-select" value={projetId || ''} onChange={e => setProjetId(Number(e.target.value))}>
                {projets.map(p => <option key={p.id} value={p.id}>{p.nom}</option>)}
              </select>
            </div>
          )}

          {projets.length === 0 && !loading && (
            <div className="suivi-empty">
              <p>Aucun projet. Creez un projet dans l'onglet Planning pour commencer.</p>
            </div>
          )}

          {loading ? (
            <div className="suivi-loading">Chargement...</div>
          ) : (
            <div className="suivi-phases">
              {phases.map(phase => {
                const phaseActions = actions.filter(a => a.phase_id === phase.id);
                const isCollapsed = collapsed[phase.id];
                return (
                  <div key={phase.id} className="suivi-phase">
                    <div className="suivi-phase-header" style={{ borderLeftColor: phase.couleur }}
                      onClick={() => toggleCollapse(phase.id)}>
                      <span className="suivi-chevron">{isCollapsed ? '▶' : '▼'}</span>
                      <span className="suivi-phase-name">{phase.nom}</span>
                      <span className="suivi-phase-count">{phaseActions.length} action(s)</span>
                    </div>

                    {!isCollapsed && phaseActions.map(action => {
                      const actionTaches = taches.filter(t => t.action_id === action.id);
                      const doneCount = actionTaches.filter(t => t.statut === 'fait').length;
                      const totalCount = actionTaches.length;
                      const progressPct = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;
                      const statutInfo = ACTION_STATUTS.find(s => s.id === action.statut);

                      return (
                        <div key={action.id} className="suivi-action">
                          <div className="suivi-action-header">
                            <div className="suivi-action-left">
                              {action.vigilance && <span className="suivi-vig">!</span>}
                              {action.jalon && <span className="suivi-jalon">&#9670;</span>}
                              <span className="suivi-action-name">{action.nom}</span>
                              <span className="suivi-action-badge" style={{ background: statutInfo?.color + '20', color: statutInfo?.color }}>
                                {statutInfo?.label}
                              </span>
                            </div>
                            <div className="suivi-action-right">
                              {action.responsable && <span className="suivi-action-resp">{action.responsable}</span>}
                              <span className="suivi-action-dates">
                                {formatDate(action.date_debut)} → {formatDate(action.date_fin)}
                              </span>
                            </div>
                          </div>

                          {/* Barre de progression */}
                          {totalCount > 0 && (
                            <div className="suivi-progress">
                              <div className="suivi-progress-bar">
                                <div className="suivi-progress-fill" style={{ width: `${progressPct}%` }} />
                              </div>
                              <span className="suivi-progress-text">{doneCount}/{totalCount}</span>
                            </div>
                          )}

                          {/* Tâches */}
                          <div className="suivi-taches">
                            {actionTaches.map(t => (
                              editingTache === t.id ? (
                                <div key={t.id} className="suivi-tache suivi-tache-editing">
                                  <input value={editTacheVal.titre || ''} placeholder="Titre"
                                    onChange={e => setEditTacheVal(v => ({ ...v, titre: e.target.value }))} />
                                  <input value={editTacheVal.responsable || ''} placeholder="Responsable"
                                    onChange={e => setEditTacheVal(v => ({ ...v, responsable: e.target.value }))} />
                                  <input type="date" value={editTacheVal.echeance?.substring(0, 10) || ''}
                                    onChange={e => setEditTacheVal(v => ({ ...v, echeance: e.target.value }))} />
                                  <select value={editTacheVal.statut || 'a_faire'}
                                    onChange={e => setEditTacheVal(v => ({ ...v, statut: e.target.value }))}>
                                    {TACHE_STATUTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                                  </select>
                                  <button className="suivi-tache-btn save" onClick={handleSaveEditTache}>✓</button>
                                  <button className="suivi-tache-btn cancel" onClick={() => setEditingTache(null)}>✕</button>
                                  <button className="suivi-tache-btn danger" onClick={() => handleDeleteTache(t.id)}>&#x1F5D1;</button>
                                </div>
                              ) : (
                                <div key={t.id} className={`suivi-tache ${t.statut === 'fait' ? 'done' : ''} ${t.statut === 'bloque' ? 'blocked' : ''}`}>
                                  <button className="suivi-tache-check" onClick={() => toggleTacheStatut(t)}>
                                    {t.statut === 'fait' ? '✓' : t.statut === 'bloque' ? '!' : '○'}
                                  </button>
                                  <span className="suivi-tache-titre">{t.titre}</span>
                                  {t.responsable && <span className="suivi-tache-resp">{t.responsable}</span>}
                                  {t.echeance && <span className="suivi-tache-date">{formatDate(t.echeance)}</span>}
                                  {isAdmin && (
                                    <button className="suivi-tache-edit" onClick={() => {
                                      setEditingTache(t.id);
                                      setEditTacheVal({ titre: t.titre, responsable: t.responsable || '', echeance: t.echeance || '', statut: t.statut });
                                    }}>✎</button>
                                  )}
                                </div>
                              )
                            ))}

                            {/* Ajouter tâche */}
                            {addingTacheFor === action.id ? (
                              <div className="suivi-tache suivi-tache-adding">
                                <input value={newTache.titre} placeholder="Titre de la tache" autoFocus
                                  onChange={e => setNewTache(v => ({ ...v, titre: e.target.value }))}
                                  onKeyDown={e => { if (e.key === 'Enter') handleAddTache(action.id); }} />
                                <input value={newTache.responsable} placeholder="Responsable"
                                  onChange={e => setNewTache(v => ({ ...v, responsable: e.target.value }))} />
                                <input type="date" value={newTache.echeance}
                                  onChange={e => setNewTache(v => ({ ...v, echeance: e.target.value }))} />
                                <button className="suivi-tache-btn save" onClick={() => handleAddTache(action.id)}>✓</button>
                                <button className="suivi-tache-btn cancel" onClick={() => setAddingTacheFor(null)}>✕</button>
                              </div>
                            ) : (
                              isAdmin && (
                                <button className="suivi-add-tache" onClick={() => { setAddingTacheFor(action.id); setNewTache({ titre: '', responsable: '', echeance: '' }); }}>
                                  + Tache
                                </button>
                              )
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {!isCollapsed && phaseActions.length === 0 && (
                      <div className="suivi-empty-phase">Aucune action dans cette phase.</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
